import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Section } from '../common/Section';
import { Eyebrow } from '../common/Eyebrow';
import { SplitHeading } from '../animation/SplitHeading';
import { Reveal } from '../animation/Reveal';
import { Icon } from '../common/Icon';
import { productRepository } from '../../services/productRepository';
import { primaryImage } from '../../lib/image';
import { accentAlpha } from '../../lib/slug';
import { cn } from '../../lib/cn';
import { useCropSolutions } from '../../hooks';

export function CropFinder() {
  const { data: cropsAll = [] } = useCropSolutions();
  const crops = cropsAll.slice(0, 8);
  const [active, setActive] = useState<string | undefined>(undefined);
  const activeSlug = active ?? crops[0]?.slug;
  const crop = crops.find((c) => c.slug === activeSlug) ?? crops[0];
  const recommended = (crop?.productSlugs ?? [])
    .map((s) => productRepository.getProductBySlugSync(s))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  const Header = (
    <div className="mb-10 max-w-2xl">
      <Eyebrow>Crop solutions</Eyebrow>
      <SplitHeading as="h2" text="Find the right products for your crop." className="mt-4 font-display text-h1 font-semibold text-ink" />
      <Reveal as="p" className="mt-4 text-lead text-ink-500" y={16}>
        Pick a crop to see products matched from our verified, crop-by-crop application programmes.
      </Reveal>
    </div>
  );

  // No crop tags entered on any product yet — show only the intro (graceful, not fake).
  if (!crop) {
    return (
      <Section className="section bg-sand-100/60" containerClassName="">
        {Header}
      </Section>
    );
  }

  return (
    <Section className="section bg-sand-100/60" containerClassName="">
      {Header}

      <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="flex flex-wrap gap-2.5 lg:content-start" role="tablist" aria-label="Choose a crop">
          {crops.map((c) => {
            const isActive = c.slug === activeSlug;
            return (
              <button
                key={c.slug}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(c.slug)}
                className={cn(
                  'rounded-full border px-5 py-2.5 text-sm font-medium transition-all duration-300 ease-premium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-bright',
                  isActive
                    ? 'border-transparent bg-brand-600 text-white shadow-soft'
                    : 'border-sand-200 bg-white text-ink-700 hover:border-brand-400 hover:text-brand-600',
                )}
              >
                {c.name}
              </button>
            );
          })}
        </div>

        <div key={activeSlug} className="rounded-xl border border-sand-200 bg-white p-7 shadow-soft animate-fade-up">
          <h3 className="font-display text-2xl font-semibold text-ink">{crop.name}</h3>
          {crop.intro && <p className="mt-2 text-[0.95rem] leading-relaxed text-ink-500">{crop.intro}</p>}

          {recommended.length > 0 && (
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {recommended.map((p) => {
                const img = primaryImage(p);
                return (
                  <Link
                    key={p.id}
                    to={`/catalog/${p.slug}`}
                    className="group flex flex-col items-center rounded-lg border border-sand-200 p-3 text-center transition-shadow hover:shadow-soft"
                    style={{ background: `radial-gradient(120% 90% at 50% 0%, ${accentAlpha(p.accentColor, 0.12)}, #ffffff 70%)` }}
                  >
                    <span className="flex h-16 w-16 items-center justify-center">
                      {img ? (
                        <img src={img} alt={p.name} className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                      ) : (
                        <Icon name="Droplets" className="h-7 w-7 text-ink-300" />
                      )}
                    </span>
                    <span className="mt-2 text-xs font-semibold text-ink">{p.name}</span>
                  </Link>
                );
              })}
            </div>
          )}

          {crop.note && (
            <p className="mt-5 flex items-start gap-2 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">
              <Icon name="Info" className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
              {crop.note}
            </p>
          )}

          <Link to={`/crop-solutions/${crop.slug}`} className="link-underline mt-6 inline-flex items-center gap-2 font-semibold text-brand-600">
            Full {crop.name} programme <Icon name="ArrowRight" className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Reveal className="mt-8" y={16}>
        <Link to="/crop-solutions" className="link-underline inline-flex items-center gap-2 font-semibold text-ink-700">
          See all crop solutions <Icon name="ArrowRight" className="h-4 w-4" />
        </Link>
      </Reveal>
    </Section>
  );
}
